import { useState, useEffect } from "react"

import {
  Container,
  AppBar,
  Toolbar,
  Box,
  Button,
  Stack,
  Link as MuiLink,
  IconButton,
} from "@mui/material"

import { NavLink, useLocation, useNavigate } from "react-router-dom"
import MenuIcon from "@mui/icons-material/Menu"
import { ReactComponent as CalendarIcon } from "../../assets/imgs/icons/calendar.svg"
import { ReactComponent as LogoutIcon } from "../../assets/imgs/icons/logout.svg"
import { ReactComponent as PersonIcon } from "../../assets/imgs/icons/person.svg"

import logo from "../../assets/imgs/logos/PrymeBookingLogo-White.svg"
import { menuItems } from "../../constants/menuItems"
import useDeviceType from "../../utils/useDeviceType"
import { getUserProfiles } from "../../api/profile"
import { API_DOMAIN } from "../../config/apiDomain"

import UserMenu from "./UserMenu"
import HeaderMobileDrawer from "./HeaderMobileDrawer"

export default function Header() {
  const location = useLocation()
  const currentPath = location.pathname
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [, setAnchorEl] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userData, setUserData] = useState(null)

  const profileImage = userData?.image_profile_url
    ? `${API_DOMAIN}${userData.image_profile_url}`
    : null

  const { isMobile } = useDeviceType()
  const closeDrawer = () => setDrawerOpen(false)

  useEffect(() => {
    const token = sessionStorage.getItem("token")
    if (token) {
      setIsLoggedIn(true)

      getUserProfiles()
        .then((profile) => {
          setUserData(profile)
        })
        .catch((err) => {
          console.error("Failed to fetch profile:", err)
        })
    }
  }, [])

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const goToMain = () => {
    navigate("/dashboard?tab=main")
    handleMenuClose()
    closeDrawer()
  }
  const goToProfile = () => {
    navigate("/dashboard?tab=viewProfile")
    handleMenuClose()
  }

  const goToBookings = () => {
    navigate("/dashboard?tab=myBookings")
    handleMenuClose()
  }

  const goToDashboardTab = (tab) => {
    navigate(`/dashboard?tab=${tab}`)
    handleMenuClose()
    closeDrawer()
  }

  const handleCustomerSignIn = () => {
    navigate("/login?role=customer")
  }

  const handleLogout = () => {
    sessionStorage.removeItem("token")
    setIsLoggedIn(false)
    handleMenuClose()
    navigate("/home")
  }

  const headerMenuItems = [
    {
      label: "My Account",
      icon: PersonIcon,
      action: isMobile ? goToMain : goToProfile,
    },
    {
      label: "My Bookings",
      icon: CalendarIcon,
      action: goToBookings,
    },
    { divider: true },
    {
      label: "Sign out",
      icon: LogoutIcon,
      action: handleLogout,
    },
  ]
  return (
    <AppBar
      position="static"
      sx={{
        background: (theme) => theme.palette.background?.header?.gradient,
      }}
      elevation={1}
    >
      <Container
        maxWidth="lg"
        disableGutters
        sx={{ px: { xs: 2, sm: 3, md: 0 } }}
      >
        <Toolbar
          disableGutters
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="TechPryme Logo"
            sx={{ height: 40, mr: 2 }}
          />

          <Box sx={{ display: { xs: "none", md: "flex" }, flex: 1 }}>
            <Stack
              direction="row"
              spacing={5}
              alignItems="center"
              sx={{ mx: 5 }}
            >
              {menuItems
                .filter((item) => item.label === "Home")
                .map((item) => {
                  const isActive = currentPath === item.href
                  return (
                    <MuiLink
                      key={item.label}
                      component={NavLink}
                      to={item.href}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 500,
                        color: "white",
                        paddingBottom: "4px",
                        bgcolor: isActive ? "#5341814D" : "transparent",
                        py: isActive && 1,
                        px: isActive && 2,
                        borderRadius: isActive && "8px",
                      }}
                    >
                      {item.icon}
                      <Box component="span" sx={{ ml: 1 }}>
                        {" "}
                        {item.label}{" "}
                      </Box>
                    </MuiLink>
                  )
                })}
            </Stack>
          </Box>

          <Box>
            {!isLoggedIn ? (
              <Button
                variant="contained"
                onClick={handleCustomerSignIn}
                sx={{
                  color: "white",
                  backgroundColor: "#FC3468",
                  "&:hover": { backgroundColor: "#ff0044ff" },
                  ml: { xs: 30 },
                  display: { xs: "none", sm: "flex" },
                }}
              >
                Sign in
              </Button>
            ) : (
              <>
                {!isMobile && (
                  <UserMenu
                    userData={userData}
                    profilePic={profileImage}
                    goToProfile={goToProfile}
                    goToMain={goToMain}
                    goToBookings={goToBookings}
                    handleLogout={handleLogout}
                    goToDashboardTab={goToDashboardTab}
                    userMenuItems={headerMenuItems}
                  />
                )}
              </>
            )}
          </Box>

          {isMobile && (
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
                <MenuIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        <HeaderMobileDrawer
          drawerOpen={drawerOpen}
          isLoggedIn={isLoggedIn}
          userData={userData}
          handleLogout={handleLogout}
          onClose={closeDrawer}
          goToDashboardTab={goToDashboardTab}
        />
      </Container>
    </AppBar>
  )
}
